import React, { useState, useEffect } from 'react';

// Simplified component to display CloudFront-served Image Thumbnail
const VideoThumbnailGenerator = ({ thumbnailSrc, alt, style }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!thumbnailSrc) return;

    // If it's already a full URL (legacy), use as-is
    if (thumbnailSrc.startsWith('http')) {
      setImageUrl(thumbnailSrc);
      return;
    }

    // Fetch a short-lived signed CloudFront URL from the backend
    fetch(`${backendUrl}/api/signed-url/${encodeURIComponent(thumbnailSrc)}`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) => setImageUrl(data.signedUrl))
      .catch(() => setImageUrl(null));
  }, [thumbnailSrc, backendUrl]);

  return (
    <img
      src={imageUrl || ''}
      alt={alt}
      style={{ ...style, objectFit: 'cover' }}
      loading="lazy"
      onError={(e) => {
        // Fallback styling if image fails to load
        e.target.style.backgroundColor = '#ccc';
      }}
    />
  );
};

// Updated Exercise Card Component
const ExerciseCardWithDynamicThumbnail = ({ exercise, onPlay, categoryColor }) => (
  <article
    className="video-card"
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid #e9ecef',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      minWidth: 260,
      scrollSnapAlign: 'start',
      ...(categoryColor && { borderColor: categoryColor + '40' })
    }}
    onClick={() => onPlay(exercise)}
  >
    <div style={{ position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden' }}>
      {/* CloudFront-served Image Thumbnail */}
      <VideoThumbnailGenerator
        thumbnailSrc={exercise.thumbnailSrc}
        alt={`${exercise.title} thumbnail`}
        style={{
          width: '100%',
          height: '100%',
          transition: 'transform 0.3s ease'
        }}
      />

      {/* Play Overlay */}
      <div className="play-overlay" style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.3s ease'
      }}>
        <div
          className="play-button"
          style={{
            width: 60,
            height: 60,
            backgroundColor: categoryColor || '#FF9933',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: '#ffffff',
            transform: 'scale(0.8)',
            transition: 'transform 0.3s ease',
            border: '2px solid #ffffff'
          }}
        >
          ▶
        </div>
      </div>

      {/* Duration Badge */}
      <span style={{
        position: 'absolute',
        bottom: '0.5rem',
        right: '0.5rem',
        backgroundColor: '#000080',
        color: '#ffffff',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.8rem',
        fontWeight: 500
      }}>
        {exercise.duration}
      </span>
    </div>

    <div style={{ padding: '1rem' }}>
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        color: '#000080',
        lineHeight: 1.3
      }}>
        {exercise.title}
      </h3>
      <p style={{
        fontSize: '0.95rem',
        color: '#666666',
        lineHeight: 1.5,
        marginBottom: '0.75rem'
      }}>
        {exercise.description}
      </p>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap'
      }}>
        <span style={{
          padding: '0.25rem 0.6rem',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#ffffff',
          backgroundColor: getDifficultyColor(exercise.difficulty)
        }}>
          {exercise.difficulty}
        </span>
        
        {/* I REMOVED THE CATEGORY TEXT HERE */}
      
      </div>
    </div>
  </article>
);

// Helper function for difficulty colors
const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'Beginner': return '#4CAF50';
    case 'Intermediate': return '#FF9800';
    case 'Advanced': return '#F44336';
    default: return '#9E9E9E';
  }
};

export {
  VideoThumbnailGenerator,
  ExerciseCardWithDynamicThumbnail
};